import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { applicationApi } from '../api/application';
import { LicenseUsageDetail, ApplicationOwner } from '../types/application';

interface LicenseQuotaProps {
  owner?: ApplicationOwner | null;
  onOwnerUpdate?: () => void;
}

export const LicenseQuota: React.FC<LicenseQuotaProps> = ({ owner, onOwnerUpdate }) => {
  const [licenseUsage, setLicenseUsage] = useState<LicenseUsageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLicenseUsage = async () => {
    if (!owner?.owner_id) return;

    try {
      setLoading(true);
      setError(null);
      const usage = await applicationApi.getLicenseUsageDetail(owner.owner_id);
      // applications_detailが存在しない場合は空配列を設定
      setLicenseUsage({
        ...usage,
        applications_detail: usage.applications_detail || []
      });
    } catch (err: any) {
      console.error('Failed to fetch license usage:', err);
      setError(err.response?.data?.error || 'ライセンス使用状況の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const syncLicenseQuota = async () => {
    if (!owner?.owner_id) return;

    try {
      setSyncing(true);
      setError(null);
      await applicationApi.syncLicenseQuota(owner.owner_id);
      await fetchLicenseUsage();
      onOwnerUpdate?.(); // オーナー情報も更新
    } catch (err: any) {
      console.error('Failed to sync license quota:', err);
      setError(err.response?.data?.error || 'ライセンス枠同期に失敗しました');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchLicenseUsage();
  }, [owner?.owner_id]);

  if (!owner) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="info">
          ライセンス枠を表示するには、まずオーナー情報を設定してください。
        </Alert>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          <Typography>ライセンス使用状況を取得中...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchLicenseUsage}>
              再試行
            </Button>
          }
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!licenseUsage) {
    return null;
  }

  const usagePercentage = licenseUsage.license_quota_total > 0
    ? (licenseUsage.license_quota_used / licenseUsage.license_quota_total) * 100
    : 0;

  const getUsageColor = () => {
    if (usagePercentage >= 90) return 'error';
    if (usagePercentage >= 70) return 'warning';
    return 'success';
  };

  const isQuotaExpired = licenseUsage.is_quota_expired;
  const lastUpdated = licenseUsage.last_updated
    ? new Date(licenseUsage.last_updated).toLocaleString('ja-JP')
    : '未設定';

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="h2">
          ライセンス使用状況
        </Typography>
        <Box display="flex" gap={1}>
          {isQuotaExpired && (
            <Tooltip title="ライセンス枠情報が24時間以上更新されていません">
              <WarningIcon color="warning" />
            </Tooltip>
          )}
          <Button
            startIcon={syncing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={syncLicenseQuota}
            disabled={syncing}
            size="small"
          >
            同期
          </Button>
        </Box>
      </Box>

      {isQuotaExpired && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ライセンス枠情報が最新でない可能性があります。「同期」ボタンでサブスクライトから最新情報を取得してください。
        </Alert>
      )}

      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="text.secondary">
            使用状況
          </Typography>
          <Typography variant="body2">
            {licenseUsage.license_quota_used} / {licenseUsage.license_quota_total} ライセンス
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={usagePercentage}
          color={getUsageColor()}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Typography variant="caption" color="text.secondary">
            利用可能: {licenseUsage.license_quota_available} ライセンス
          </Typography>
          <Typography variant="caption" color="text.secondary">
            最終更新: {lastUpdated}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <Chip
          label={`総数: ${licenseUsage.license_quota_total}`}
          color="default"
          variant="outlined"
        />
        <Chip
          label={`使用中: ${licenseUsage.license_quota_used}`}
          color={getUsageColor()}
          icon={<CheckCircleIcon />}
        />
        <Chip
          label={`利用可能: ${licenseUsage.license_quota_available}`}
          color="success"
          variant="outlined"
        />
        <Chip
          label={`アプリケーション: ${licenseUsage.total_applications}`}
          color="info"
          icon={<InfoIcon />}
        />
      </Box>

      {licenseUsage.applications_detail && licenseUsage.applications_detail.length > 0 && (
        <Box>
          <Button
            startIcon={<ExpandMoreIcon sx={{ transform: showDetails ? 'rotate(180deg)' : 'none' }} />}
            onClick={() => setShowDetails(!showDetails)}
            size="small"
            sx={{ mb: 1 }}
          >
            アプリケーション別詳細
          </Button>
          <Collapse in={showDetails}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>アプリケーション名</TableCell>
                    <TableCell align="center">アクティブライセンス数</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(licenseUsage.applications_detail || []).map((app) => (
                    <TableRow key={app.application_id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{app.application_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {app.application_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={app.active_licenses}
                          size="small"
                          color={app.active_licenses > 0 ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>
      )}

      {licenseUsage.license_quota_available === 0 && licenseUsage.license_quota_total > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          ライセンス枠を使い切っています。新しいライセンスを発行するには、サブスクライトで「ソフトウェアライセンス認証機能」を追加購入してください。
        </Alert>
      )}
    </Paper>
  );
};